import { createContext, Dispatch } from 'react';
import { JSONSchema7 } from 'json-schema';
import { WorkflowLockStatus } from './types';
import { resetConditionalControls } from './ControlReducerUtils/resetConditionalControls';
import controlAdd from './ControlReducerUtils/controlAdd';
import controlDelete from './ControlReducerUtils/controlDelete';
import controlMove from './ControlReducerUtils/controlMove';
import controlSetBuilding from './ControlReducerUtils/controlSetBuilding';
import controlsSetVisibility from './ControlReducerUtils/controlsSetVisibility';
import ControlState from '../Shared/controlState';
import controlUpdate from './ControlReducerUtils/controlUpdate';
import PageState from '../Shared/pageState';
import pageUpdate from './ControlReducerUtils/pageUpdate';
import pageUpdateLatestInfo from './ControlReducerUtils/pageUpdateLatestInfo';
import reorderPages from './ControlReducerUtils/reorderPages';
import PageValidState from '../Shared/pageValidState';
import pageValidUpdate from './ControlReducerUtils/pageValidUpdate';
import setDefaultValidPage from './ControlReducerUtils/setDefaultValidPage';

export const workflowEngineReducer = (
  state: WorkflowEngineState,
  action: WorkflowEngineAction
) => {
  const { type, payload } = action;
  switch (type) {
    case WorkflowEngineActionKind.CONTROLS_SET_VISIBLE: {
      return controlsSetVisibility(state, payload as ControlsVisibility);
    }
    case WorkflowEngineActionKind.CONTROL_ADD: {
      return controlAdd(state, payload as ControlState);
    }
    case WorkflowEngineActionKind.CONTROL_UPDATE: {
      return controlUpdate(state, payload as ControlState);
    }
    case WorkflowEngineActionKind.CONTROL_DELETE: {
      return controlDelete(state, payload as ControlState);
    }
    case WorkflowEngineActionKind.CONTROL_OPEN_BUILDING: {
      return controlSetBuilding(state, payload as ControlState, true);
    }
    case WorkflowEngineActionKind.CONTROL_CLOSE_BUILDING: {
      return controlSetBuilding(state, payload as ControlState, false);
    }
    case WorkflowEngineActionKind.CONTROL_MOVE: {
      return controlMove(state, payload as SwitchComponent);
    }
    case WorkflowEngineActionKind.PAGE_UPDATE: {
      return pageUpdate(state, payload as PageState);
    }
    case WorkflowEngineActionKind.SET_LAST_UPDATED_INFO:
      return pageUpdateLatestInfo(state, payload as LastUpdatedInfo);
    case WorkflowEngineActionKind.SWITCH_TO_EDIT_MODE:
      return {
        ...state,
        mode: WorkflowEngineMode.BUILD,
        versionExternalId: payload as string
      };
    case WorkflowEngineActionKind.SET_WORKFLOW_DATA:
      return {
        ...state,
        workflowData: payload as Record<string, unknown>[]
      };
    case WorkflowEngineActionKind.SET_EVENT_DATA: {
      return {
        ...state,
        eventData: payload as EventData
      };
    }
    case WorkflowEngineActionKind.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: payload as number
      };
    case WorkflowEngineActionKind.SET_PREVIEW_MODE:
      return {
        ...state,
        mode: WorkflowEngineMode.PREVIEW,
        currentPage: 0,
        hasAccessToWorkflow: true,
        pages: resetConditionalControls(state.pages),
        workflowData: []
      };
    case WorkflowEngineActionKind.SET_BUILD_MODE:
      return {
        ...state,
        mode: WorkflowEngineMode.BUILD
      };
    case WorkflowEngineActionKind.SET_MAPPER_MODE:
      return {
        ...state,
        mode: WorkflowEngineMode.MAPPER
      };
    case WorkflowEngineActionKind.FIRST_LOAD:
      return {
        ...state,
        ...(payload as WorkflowEngineState)
      };
    case WorkflowEngineActionKind.ADD_PAGE:
      return {
        ...state,
        pages: [...state.pages, payload as PageState]
      };
    case WorkflowEngineActionKind.DELETE_PAGE: {
      const pageId = payload as string;
      return {
        ...state,
        pages: [...state.pages.filter((p) => p.id !== pageId)]
      };
    }
    case WorkflowEngineActionKind.SET_READ_ONLY_VIEW:
      return {
        ...state,
        isReadOnlyView: payload as boolean
      };
    case WorkflowEngineActionKind.SET_WORKFLOW_COMPLETED:
      return {
        ...state,
        isWorkflowCompleted: payload as boolean
      };
    case WorkflowEngineActionKind.HIDE_COMPLETION_SCREEN:
      return {
        ...state,
        isWorkflowCompleted: false
      };
    case WorkflowEngineActionKind.SET_WORKFLOW_PAUSED_UNPAUSED:
      return {
        ...state,
        isPaused: payload as boolean
      };
    case WorkflowEngineActionKind.SET_ROLE:
      return {
        ...state,
        isWorkflowCompleted: false,
        role: payload as string
      };
    case WorkflowEngineActionKind.SET_ACCESS:
      return {
        ...state,
        hasAccessToWorkflow: payload as boolean
      };
    case WorkflowEngineActionKind.RESTORE_PREVIEW_ACCESS:
      return {
        ...state,
        currentPage: 0,
        hasAccessToWorkflow: true,
        isWorkflowCompleted: false
      };
    case WorkflowEngineActionKind.SET_WORKFLOW_LOCK_STATUS: {
      const lockStatus = payload as WorkflowLockStatus;
      const isDifferent =
        JSON.stringify(lockStatus) !== JSON.stringify(state.lockStatus);
      return lockStatus && isDifferent ? { ...state, lockStatus } : state;
    }
    case WorkflowEngineActionKind.SET_STATUS_ENABLE:
      return {
        ...state,
        isStatusEnabled: payload as boolean
      };
    case WorkflowEngineActionKind.REORDER_PAGES:
      return reorderPages(state, payload as UpdatePages);
    case WorkflowEngineActionKind.WORKFLOW_NAME_UPDATE:
      return {
        ...state,
        workflowName: payload as string
      };
    case WorkflowEngineActionKind.UPDATE_PAGE_VALID:
      return pageValidUpdate(state, payload as PageValidState);
    case WorkflowEngineActionKind.SET_PAGE_VALID:
      return setDefaultValidPage(state);
    default:
      return state;
  }
};

export enum WorkflowEngineActionKind {
  FIRST_LOAD,
  ADD_PAGE,
  DELETE_PAGE,
  CONTROL_ADD,
  CONTROL_UPDATE,
  CONTROL_OPEN_BUILDING,
  CONTROL_CLOSE_BUILDING,
  CONTROL_DELETE,
  CONTROL_MOVE,
  SET_PREVIEW_MODE,
  SET_BUILD_MODE,
  SET_MAPPER_MODE,
  SET_CURRENT_PAGE,
  SET_EVENT_DATA,
  PAGE_UPDATE,
  SET_READ_ONLY_VIEW,
  SET_LAST_UPDATED_INFO,
  SET_WORKFLOW_DATA,
  SET_WORKFLOW_COMPLETED,
  HIDE_COMPLETION_SCREEN,
  SET_WORKFLOW_PAUSED_UNPAUSED,
  SET_ROLE,
  SET_ACCESS,
  RESTORE_PREVIEW_ACCESS,
  SWITCH_TO_EDIT_MODE,
  SET_WORKFLOW_LOCK_STATUS,
  SET_STATUS_ENABLE,
  CONTROLS_SET_VISIBLE,
  REORDER_PAGES,
  WORKFLOW_NAME_UPDATE,
  UPDATE_PAGE_VALID,
  SET_PAGE_VALID,
  UPDATE_IS_FORM_DIRTY
}

export interface WorkflowEngineState {
  externalId: string;
  currentPage: number;
  mode: WorkflowEngineMode;
  pages: PageState[];
  role: string;
  versionExternalId: string;
  workflowName: string;
  workflowType: string;
  workflowEntryExternalId?: string;
  eventData?: EventData;
  isReadOnlyView: boolean;
  hasAccessToWorkflow: boolean;
  versionData?: VersionData;
  lastUpdatedDate: string | null;
  lastUpdatedBy: string | null;
  isPaused: boolean;
  workflowData: Record<string, unknown>[];
  isWorkflowCompleted: boolean;
  publishedVersionExternalId?: string;
  lockStatus?: WorkflowLockStatus;
  isStatusEnabled: boolean;
  pagesValidState: PageValidState[];
}

export interface EventData {
  [key: string]: number | string | null;
}

export interface SwitchComponent {
  control: ControlState;
  targetPageId: string;
}

export interface VersionData {
  createdDate: string;
  datePublished: string;
  isPublished: boolean;
  publishedBy: string;
  releaseNotes: string;
  version: number;
}

export interface LastUpdatedInfo {
  externalId: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  metadata: string;
}

export interface UpdatePages {
  newPages: PageState[];
  lastModifyDate: string;
  lastModifyBy: string;
}

export interface ControlsVisibility {
  show: string[];
  hide: string[];
}

export interface WorkflowEngineAction {
  type: WorkflowEngineActionKind;
  payload?:
    | boolean
    | JSONSchema7
    | string
    | PageState
    | PageState[]
    | ControlState
    | WorkflowEngineState
    | number
    | EventData
    | SwitchComponent
    | LastUpdatedInfo
    | Record<string, unknown>[]
    | WorkflowLockStatus
    | ControlsVisibility
    | UpdatePages
    | PageValidState;
}

export enum WorkflowEngineMode {
  PREVIEW,
  BUILD,
  ENTRY,
  MAPPER
}

export const initialState: WorkflowEngineState = {
  externalId: '',
  mode: WorkflowEngineMode.BUILD,
  versionExternalId: '',
  workflowName: '',
  pages: [],
  currentPage: 0,
  role: '',
  workflowType: '',
  workflowEntryExternalId: undefined,
  eventData: undefined,
  isReadOnlyView: true,
  hasAccessToWorkflow: true,
  versionData: undefined,
  lastUpdatedDate: null,
  lastUpdatedBy: null,
  workflowData: [],
  isWorkflowCompleted: false,
  isPaused: false,
  lockStatus: undefined,
  isStatusEnabled: false,
  pagesValidState: []
};

export const WorkflowEngineContext = createContext<{
  state: WorkflowEngineState;
  dispatch: Dispatch<WorkflowEngineAction>;
}>({
  state: initialState,
  dispatch: () => undefined
});
